// src/services/api.ts
import axios, {AxiosError} from 'axios'
import {
    User, Transaction, DashboardStats, Wallet,
    LoginResponse, ApiResponse, UserActivity
} from '../types'

const API_URL = (import.meta.env.VITE_API_URL as string) || 'https://transfer-backend.mrad-mohammad.workers.dev'

export const api = axios.create({
    baseURL: API_URL,
    headers: {'Content-Type': 'application/json'},
    timeout: 30000
})

// Request interceptor - add token
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token')
    const tokenType = localStorage.getItem('token_type')

    if (token) {
        config.headers.Authorization = `Bearer ${token}`
    }

    console.log('📡 Request:', {
        url: config.url,
        method: config.method,
        hasToken: !!token,
        tokenType: tokenType || 'unknown',
        tokenPreview: token ? `${token.substring(0, 15)}...` : null
    })

    return config
})

// Response interceptor - handle errors
api.interceptors.response.use(
    (response) => {
        console.log(`✅ ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`)
        return response
    },
    (error: AxiosError) => {
        console.error('❌ Response Error:', {
            url: error.config?.url,
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data
        })

        if (error.response?.status === 401) {
            const url = error.config?.url || ''
            console.log('🔴 401 Unauthorized on:', url)

            const token = localStorage.getItem('token')
            console.log('📦 Current token:', token ? `${token.substring(0, 20)}...` : 'No token')

            if (url === '/api/admin/login') {
                localStorage.removeItem('token')
                localStorage.removeItem('token_type')
                localStorage.removeItem('user')

                if (!window.location.pathname.includes('/login')) {
                    window.location.href = '/login'
                }
            } else {
                console.log('⚠️ 401 on non-login endpoint - NOT logging out')
            }
        }

        if (error.response?.status === 403) {
            console.warn('⛔ Access forbidden - insufficient permissions')
        }

        return Promise.reject(error)
    }
)

// ========== Auth ==========
export const login = async (username: string, password: string): Promise<LoginResponse> => {
    try {
        console.log('🔐 Attempting login for user:', username)

        const response = await api.post('/api/admin/login', {
            username,
            password,
            device_type: 'web'
        })

        console.log('📥 Login response:', response.data)

        const apiResponse = response.data

        if (apiResponse.success && apiResponse.data) {
            const token = apiResponse.data.token
            const user = apiResponse.data.user

            console.log('✅ Login successful!')
            console.log('📦 Token to save:', token ? `${token.substring(0, 20)}...` : 'No token')
            console.log('👤 User:', user)

            localStorage.setItem('token', token)
            localStorage.setItem('token_type', 'admin')
            localStorage.setItem('user', JSON.stringify(user))

            const savedToken = localStorage.getItem('token')
            const savedUser = localStorage.getItem('user')

            console.log('💾 Verification after save:', {
                tokenSaved: !!savedToken,
                tokenPreview: savedToken ? `${savedToken.substring(0, 15)}...` : null,
                userSaved: !!savedUser
            })

            await new Promise(resolve => setTimeout(resolve, 100))

            const verifyAgain = localStorage.getItem('token')
            console.log('🔍 Second verification:', !!verifyAgain)
        } else {
            console.warn('⚠️ Login response missing data:', apiResponse)
        }

        return apiResponse
    } catch (error) {
        console.error('❌ Login error:', error)
        throw error
    }
}

export const logout = (): void => {
    console.log('🔓 Logging out')
    const token = localStorage.getItem('token')
    console.log('📦 Token being removed:', token ? `${token.substring(0, 15)}...` : 'No token')

    localStorage.removeItem('token')
    localStorage.removeItem('token_type')
    localStorage.removeItem('user')

    console.log('💾 After logout:', {
        token: localStorage.getItem('token'),
        user: localStorage.getItem('user')
    })
}

export const getCurrentUser = (): User | null => {
    const user = localStorage.getItem('user')
    if (!user) return null

    try {
        return JSON.parse(user)
    } catch (e) {
        console.error('Error parsing user from localStorage:', e)
        return null
    }
}

// ========== Authentication check ==========
export const isAuthenticated = (): boolean => {
    const token = localStorage.getItem('token')
    const tokenType = localStorage.getItem('token_type')

    console.log('🔍 Checking authentication:', {
        hasToken: !!token,
        tokenType: tokenType || 'unknown',
        tokenPreview: token ? `${token.substring(0, 10)}...` : null,
        path: window.location.pathname
    })

    return !!token
}

// ========== Helper function للتحقق من حالة المصادقة ==========
export const checkAuthStatus = (): {isAuthenticated: boolean, tokenType: string | null, user: User | null} => {
    const token = localStorage.getItem('token')
    const tokenType = localStorage.getItem('token_type')
    const userStr = localStorage.getItem('user')
    let user = null

    try {
        user = userStr ? JSON.parse(userStr) : null
    } catch (e) {
        console.error('Error parsing user:', e)
    }

    return {
        isAuthenticated: !!token,
        tokenType: tokenType || null,
        user
    }
}

// ========== للتشخيص - سجل حالة المصادقة عند تحميل الصفحة ==========
if (typeof window !== 'undefined') {
    console.log('🏁 API Service initialized')
    console.log('🔍 Initial auth status:', checkAuthStatus())

    window.addEventListener('storage', (e) => {
        if (e.key === 'token' || e.key === 'user' || e.key === 'token_type') {
            console.log(`🔄 localStorage changed: ${e.key}`, {
                oldValue: e.oldValue ? 'exists' : null,
                newValue: e.newValue ? 'exists' : null
            })
        }
    })
}

// ========== Profile ==========
export const getProfile = async (): Promise<any> => {
    try {
        const response = await api.get('/api/user/profile')
        return response.data
    } catch (error) {
        console.error('Error fetching profile:', error)
        throw error
    }
}